import { Check, Minus } from 'lucide-react';
import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { cn } from '@/ux-common/lib/utils';

/**
 * Checkbox component - Minimal React wrapper for behavioral concerns.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - Radix primitive for accessible state management (data-state, aria-checked, keyboard)
 * - Ref forwarding to the underlying button element
 * - Icon rendering: Check for checked, Minus for indeterminate
 * - Optional label rendering with auto-generated ID association
 * - `data-component="Checkbox"` for component identification
 * - `className` merging via `cn()`
 *
 * The `.checkbox` base class is applied automatically — `className` composes
 * modifiers and utilities on top of it.
 *
 * @example
 * ```tsx
 * // Standalone checkbox (gets .checkbox .checkbox-default)
 * <Checkbox checked={value} onCheckedChange={setValue} />
 *
 * // With built-in label
 * <Checkbox
 *   label="Accept terms"
 *   checked={agreed}
 *   onCheckedChange={setAgreed}
 * />
 * ```
 */

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  /** Optional label text rendered as a sibling <label> element */
  label?: string;
};

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, label, id, checked, ...props }, ref) => {
    const generatedId = React.useId();
    const checkboxId = id || generatedId;

    const checkbox = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        data-component="Checkbox"
        className={cn('checkbox', className)}
        checked={checked}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="checkbox-indicator">
          {checked === 'indeterminate' ? (
            <Minus aria-hidden="true" className="glyph glyph-sm" />
          ) : (
            <Check aria-hidden="true" className="glyph glyph-sm" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    if (label) {
      return (
        <div className="checkbox-group">
          {checkbox}
          <label htmlFor={checkboxId} className="checkbox-label">
            {label}
          </label>
        </div>
      );
    }

    return checkbox;
  }
);

Checkbox.displayName = 'Checkbox';
